import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyAiUsage,
  TechnologyAiUsageUpsertPayload,
  TechnologyApplication,
  TechnologyCatalogItem,
  classifyTechnologyAiUsage,
  createTechnologyAiUsage,
  deleteTechnologyAiUsage,
  listTechnologyAiUsages,
  listTechnologyApplications,
  listTechnologyCatalogItems,
  updateTechnologyAiUsage,
} from '../../services/technologyApi';
import {
  TechnologyProductApplicationLink,
  listTechnologyProductApplicationLinks,
} from '../../services/technologyDeploymentsApi';
import { InfoList } from './InfoList';
import { TechnologyInfoSection } from './TechnologyInfoSection';
import {
  TECHNOLOGY_AI_AUTONOMY_OPTIONS,
  TECHNOLOGY_AI_CRITICALITY_OPTIONS,
  TECHNOLOGY_AI_DATA_OPTIONS,
  TECHNOLOGY_AI_IMPACT_OPTIONS,
  TECHNOLOGY_AI_RISK_CLASS_OPTIONS,
  TECHNOLOGY_AI_RISK_MATRIX_RULES,
  TECHNOLOGY_AI_USAGE_STATUS_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const PAGE_SIZE = 10;

function defaultReviewDate(): string {
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() + 1);
  return date.toISOString().slice(0, 10);
}

function todayInput(): string {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

const emptyForm: TechnologyAiUsageUpsertPayload = {
  name: '',
  applicationId: '',
  catalogItemId: '',
  salesProductId: '',
  businessProcessName: '',
  purpose: '',
  dataClassification: 'None',
  autonomyLevel: 'Assistive',
  subjectImpact: 'Low',
  processCriticality: 'Low',
  rationale: '',
  nextReviewAtUtc: defaultReviewDate(),
  status: 'Active',
};

function toDateInput(value: string): string {
  return value ? value.slice(0, 10) : '';
}

function readProblemDetail(error: unknown): string | null {
  if (typeof error !== 'object' || error == null || !('response' in error)) {
    return null;
  }

  const response = (error as { response?: { data?: { detail?: string } } }).response;
  return response?.data?.detail ?? null;
}

function riskClassTone(riskClass: string): string {
  switch (riskClass) {
    case 'Unacceptable':
      return 'bg-rose-100 text-rose-800';
    case 'High':
      return 'bg-amber-100 text-amber-900';
    case 'Limited':
      return 'bg-sky-100 text-sky-800';
    default:
      return 'bg-secondary-100 text-secondary-700';
  }
}

export function TechnologyAiModelsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.ai-models');
  const [items, setItems] = useState<TechnologyAiUsage[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [riskClassFilter, setRiskClassFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TechnologyAiUsageUpsertPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [previewClass, setPreviewClass] = useState('Minimal');
  const [applications, setApplications] = useState<TechnologyApplication[]>([]);
  const [models, setModels] = useState<TechnologyCatalogItem[]>([]);
  const [productLinks, setProductLinks] = useState<TechnologyProductApplicationLink[]>([]);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyAiUsages({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
        riskClass: riskClassFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao carregar usos de IA.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, riskClassFilter]);

  useEffect(() => {
    void listTechnologyApplications({ page: 1, pageSize: 200 })
      .then((result) => setApplications(result.items))
      .catch(() => setApplications([]));
    void listTechnologyCatalogItems({ page: 1, pageSize: 200, category: 'AiModel' })
      .then((result) => setModels(result.items))
      .catch(() => setModels([]));
  }, []);

  useEffect(() => {
    if (!form.applicationId) {
      setProductLinks([]);
      return;
    }

    void listTechnologyProductApplicationLinks({ applicationId: form.applicationId })
      .then(setProductLinks)
      .catch(() => setProductLinks([]));
  }, [form.applicationId]);

  useEffect(() => {
    if (!formExpanded) {
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void classifyTechnologyAiUsage({
        dataClassification: form.dataClassification,
        autonomyLevel: form.autonomyLevel,
        subjectImpact: form.subjectImpact,
        processCriticality: form.processCriticality,
      })
        .then((result) => {
          if (!cancelled) setPreviewClass(result.riskClass);
        })
        .catch(() => {
          if (!cancelled) setPreviewClass('');
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    form.autonomyLevel,
    form.dataClassification,
    form.processCriticality,
    form.subjectImpact,
    formExpanded,
  ]);

  function resetForm(): void {
    setEditingId(null);
    setForm({ ...emptyForm, nextReviewAtUtc: defaultReviewDate() });
    setPreviewClass('Minimal');
  }

  function startEdit(item: TechnologyAiUsage): void {
    expandForm();
    setEditingId(item.id);
    setPreviewClass(item.riskClass);
    setForm({
      name: item.name,
      applicationId: item.applicationId,
      catalogItemId: item.catalogItemId ?? '',
      salesProductId: item.salesProductId ?? '',
      businessProcessName: item.businessProcessName,
      purpose: item.purpose,
      dataClassification: item.dataClassification,
      autonomyLevel: item.autonomyLevel,
      subjectImpact: item.subjectImpact,
      processCriticality: item.processCriticality,
      rationale: item.rationale,
      nextReviewAtUtc: toDateInput(item.nextReviewAtUtc),
      status: item.status,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const payload: TechnologyAiUsageUpsertPayload = {
      ...form,
      catalogItemId: form.catalogItemId || null,
      salesProductId: form.salesProductId || null,
      nextReviewAtUtc: form.nextReviewAtUtc
        ? `${form.nextReviewAtUtc}T12:00:00.000Z`
        : '',
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyAiUsage(editingId, payload);
        toast.success('Uso de IA atualizado.');
        collapseForm(resetForm);
      } else {
        await createTechnologyAiUsage(payload);
        toast.success('Uso de IA criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao salvar uso de IA.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyAiUsage): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir uso de IA',
      message: `Deseja excluir "${item.name}" do inventário?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyAiUsage(item.id);
      toast.success('Uso de IA excluído.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao excluir uso de IA.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Modelos de IA"
        description="Usos de IA na prestação do serviço, com a classe de risco calculada pela matriz."
      />

      <TechnologyInfoSection
        title="Matriz de classificação"
        subtitle="A primeira regra que casar define a classe. Ela é gravada no cadastro e não é escolhida à mão."
        tone="violet"
      >
        <InfoList items={TECHNOLOGY_AI_RISK_MATRIX_RULES} />
      </TechnologyInfoSection>

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar uso' : 'Novo uso'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId
              ? `Editando ${form.name || 'uso selecionado'} - clique para expandir`
              : undefined
          }
          createSummary="Clique para expandir e classificar um uso de IA"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Funcionalidade
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Aplicação
                <select
                  className="erp-input"
                  value={form.applicationId}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      applicationId: event.target.value,
                      salesProductId: '',
                    })
                  }
                  required
                >
                  <option value="">Selecione</option>
                  {applications.map((application) => (
                    <option key={application.id} value={application.id}>
                      {application.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Modelo do inventário
                <select
                  className="erp-input"
                  value={form.catalogItemId ?? ''}
                  onChange={(event) => setForm({ ...form, catalogItemId: event.target.value })}
                >
                  <option value="">Nenhum</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Produto
                <select
                  className="erp-input"
                  value={form.salesProductId ?? ''}
                  onChange={(event) => setForm({ ...form, salesProductId: event.target.value })}
                  disabled={!form.applicationId}
                >
                  <option value="">Nenhum</option>
                  {productLinks.map((link) => (
                    <option key={link.id} value={link.salesProductId}>
                      {link.salesProductName || 'Produto'}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Processo de negócio
                <input
                  className="erp-input"
                  value={form.businessProcessName}
                  onChange={(event) =>
                    setForm({ ...form, businessProcessName: event.target.value })
                  }
                  required
                />
              </label>
              <label className="erp-label">
                Status
                <select
                  className="erp-input"
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value })}
                >
                  {TECHNOLOGY_AI_USAGE_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="erp-label mt-3 block">
              Finalidade
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.purpose}
                onChange={(event) => setForm({ ...form, purpose: event.target.value })}
                required
              />
            </label>

            <div className="erp-form-grid mt-3">
              <label className="erp-label">
                Dados
                <select
                  className="erp-input"
                  value={form.dataClassification}
                  onChange={(event) =>
                    setForm({ ...form, dataClassification: event.target.value })
                  }
                >
                  {TECHNOLOGY_AI_DATA_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Autonomia
                <select
                  className="erp-input"
                  value={form.autonomyLevel}
                  onChange={(event) => setForm({ ...form, autonomyLevel: event.target.value })}
                >
                  {TECHNOLOGY_AI_AUTONOMY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Impacto aos titulares
                <select
                  className="erp-input"
                  value={form.subjectImpact}
                  onChange={(event) => setForm({ ...form, subjectImpact: event.target.value })}
                >
                  {TECHNOLOGY_AI_IMPACT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Criticidade do processo
                <select
                  className="erp-input"
                  value={form.processCriticality}
                  onChange={(event) =>
                    setForm({ ...form, processCriticality: event.target.value })
                  }
                >
                  {TECHNOLOGY_AI_CRITICALITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="erp-label mt-3 block">
              Justificativa
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.rationale}
                onChange={(event) => setForm({ ...form, rationale: event.target.value })}
                required
              />
            </label>

            <div className="mt-3 flex flex-wrap items-end gap-4">
              <label className="erp-label">
                Próxima revisão
                <input
                  type="date"
                  className="erp-input"
                  min={todayInput()}
                  value={toDateInput(form.nextReviewAtUtc)}
                  onChange={(event) => setForm({ ...form, nextReviewAtUtc: event.target.value })}
                  required
                />
              </label>
              <div className="erp-label">
                Classe de risco
                <div className="mt-1">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${riskClassTone(previewClass)}`}
                  >
                    {previewClass
                      ? technologyOptionLabel(TECHNOLOGY_AI_RISK_CLASS_OPTIONS, previewClass)
                      : '—'}
                  </span>
                </div>
              </div>
            </div>

            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 mt-4 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por funcionalidade, sistema ou processo"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <select
          className="erp-input max-w-[200px]"
          value={riskClassFilter}
          onChange={(event) => {
            setPage(1);
            setRiskClassFilter(event.target.value);
          }}
        >
          <option value="">Todas as classes</option>
          {TECHNOLOGY_AI_RISK_CLASS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Funcionalidade</th>
                  <th>Sistema</th>
                  <th>Dados</th>
                  <th>Autonomia</th>
                  <th>Impacto</th>
                  <th>Criticidade</th>
                  <th>Classe</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 8 : 7} className="text-center text-secondary-500">
                      Nenhum uso de IA cadastrado.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.applicationName || '—'}</td>
                      <td>
                        {technologyOptionLabel(
                          TECHNOLOGY_AI_DATA_OPTIONS,
                          item.dataClassification,
                        )}
                      </td>
                      <td>
                        {technologyOptionLabel(TECHNOLOGY_AI_AUTONOMY_OPTIONS, item.autonomyLevel)}
                      </td>
                      <td>
                        {technologyOptionLabel(TECHNOLOGY_AI_IMPACT_OPTIONS, item.subjectImpact)}
                      </td>
                      <td>
                        {technologyOptionLabel(
                          TECHNOLOGY_AI_CRITICALITY_OPTIONS,
                          item.processCriticality,
                        )}
                      </td>
                      <td>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${riskClassTone(item.riskClass)}`}
                        >
                          {technologyOptionLabel(TECHNOLOGY_AI_RISK_CLASS_OPTIONS, item.riskClass)}
                        </span>
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
